import { useMutation } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router';

import { setAuthToken, setOtpVerified } from '@actions/sessionActions';
import styles from '@components/PageNotFoundPage.module.css';
import UnauthenticatedPage from '@components/pageTypes/UnauthenticatedPage';
import ExternalLink from '@components/typography/ExternalLink';
import InternalLink from '@components/typography/InternalLink';
import { sessionContext } from '@context/Contexts';
import {
  ClientAccessTypeEnum,
  GoogleAuthenticationCreateDocument,
  GoogleAuthenticationCreateMutation,
  GoogleAuthenticationCreateMutationVariables,
  GoogleClientTypeEnum
} from '@typing/Generated';

const GoogleOAuthCallbackPage = () => {
  const location = useLocation();
  const history = useHistory();
  const [displayError, setDisplayError] = useState<boolean>(false);

  const searchParams = new URLSearchParams(location.search);

  const [googleAuthenticationCreate, { client }] = useMutation<
    GoogleAuthenticationCreateMutation,
    GoogleAuthenticationCreateMutationVariables
  >(GoogleAuthenticationCreateDocument, {
    update: () => {
      client.resetStore();
    }
  });

  const code = searchParams.get('code');
  const scope = searchParams.get('scope');

  const { dispatch, sessionState } = useContext(sessionContext);
  const authToken = sessionState.authToken;

  useEffect(() => {
    if (code && scope) {
      const redirectUri = new URL(import.meta.env.VITE_APP_HOST);
      redirectUri.pathname = location.pathname;

      googleAuthenticationCreate({
        variables: {
          // You can't currently use Google to sign up for a paid account
          accessType: ClientAccessTypeEnum.EMPLOYER_SPONSORED,
          clientType: GoogleClientTypeEnum.WEB,
          code,
          redirectUri: redirectUri.toString(),
          scope
        }
      }).then(response => {
        const newJwt = response.data?.googleAuthenticationCreate?.session?.jwt;
        if (newJwt && newJwt !== authToken) {
          const user = response.data?.googleAuthenticationCreate?.user;

          if (user) {
            dispatch(setAuthToken(newJwt, user));

            if (user.__typename === 'Client' && user.verificationPhoneNumber?.verified) {
              dispatch(setOtpVerified(false));
            }

            const storedPathname = sessionStorage.getItem('postLoginRedirectTo');
            sessionStorage.removeItem('postLoginRedirectTo');

            if (storedPathname) {
              history.push(storedPathname);
            } else if (response.data?.googleAuthenticationCreate?.postAuthUrl) {
              window.location.href = response.data?.googleAuthenticationCreate?.postAuthUrl;
            } else {
              if (user.__typename === 'Admin') {
                history.push('/admin');
              } else if (user.__typename === 'Client') {
                history.push('/client');
              } else {
                history.push('/expert');
              }
            }
          }
        } else if (response.data?.googleAuthenticationCreate?.postAuthUrl) {
          window.location.href = response.data?.googleAuthenticationCreate?.postAuthUrl;
        } else if (
          response.data?.googleAuthenticationCreate?.errors &&
          response.data?.googleAuthenticationCreate?.errors.length > 0
        ) {
          setDisplayError(true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (displayError) {
    return (
      <UnauthenticatedPage image="activeElders">
        <div className={styles.wrapper}>
          <h1>
            <FormattedMessage id="pages.googleOAuthCallback.title" />
          </h1>
          <p>
            <FormattedMessage id="pages.googleOAuthCallback.explanationPart1" />
            <InternalLink to="/log-in">
              <FormattedMessage id="pages.googleOAuthCallback.tryAgainLink" />
            </InternalLink>
            <FormattedMessage id="pages.googleOAuthCallback.explanationPart2" />
            <ExternalLink href="mailto:support@withgrayce.com">
              <FormattedMessage id="pages.googleOAuthCallback.contactSupport" />
            </ExternalLink>
            <FormattedMessage id="pages.googleOAuthCallback.explanationPart3" />
          </p>
        </div>
      </UnauthenticatedPage>
    );
  }

  return null;
};

export default GoogleOAuthCallbackPage;
